import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * BrokerAgreementGate — uncloseable modal that appears on `/agency` if the
 * agency's `agreement_acknowledged_at` is null. Broker must acknowledge the
 * Broker Agreement (T&C) before they can onboard clients or make other
 * modifications.
 *
 * On accept, calls POST /api/agency/acknowledge-agreement, then triggers the
 * `onAcknowledged` callback so the dashboard re-fetches the profile and the
 * gate unmounts.
 */
export default function BrokerAgreementGate({ open, onAcknowledged }) {
    const [busy, setBusy] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    if (!open) return null;

    const accept = async () => {
        setBusy(true);
        try {
            await api.post("/agency/acknowledge-agreement");
            toast.success("Agreement acknowledged. You're all set.");
            onAcknowledged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Acknowledgement failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={true}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
                data-testid="broker-agreement-gate"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-primary" />
                        Broker Agreement
                    </DialogTitle>
                    <DialogDescription>
                        Please review and acknowledge the Broker Agreement before onboarding clients.
                        Scroll to the end to enable the “I acknowledge” button.
                    </DialogDescription>
                </DialogHeader>

                <div
                    className="flex-1 overflow-y-auto rounded border border-border/70 bg-secondary/20 p-4 text-xs leading-relaxed space-y-3 my-3"
                    onScroll={(e) => {
                        const el = e.currentTarget;
                        if (el.scrollHeight - el.scrollTop - el.clientHeight < 20) {
                            setScrolled(true);
                        }
                    }}
                    data-testid="broker-agreement-body"
                >
                    <p><strong>1. Nature of the Platform.</strong> Melian Dialogue provides a purely
                    gamified educational trading evaluation environment using synthetic tick simulation
                    calibrated on Nairobi Securities Exchange (NSE) volatility patterns. No live market
                    data is consumed. No real capital is at risk. The Platform is <em>not</em> a
                    stockbroker, investment adviser, or investment fund.</p>

                    <p><strong>2. Agency Onboarding.</strong> The Broker warrants that:
                    (a) they are duly licensed by the Capital Markets Authority (CMA) of Kenya;
                    (b) the CMA licence number provided during signup is accurate and current;
                    (c) each client onboarded through the Agency has consented to their information
                    being processed on the Platform for the purpose of training and evaluation.</p>

                    <p><strong>3. Subscription.</strong> The Institutional subscription is KES 50,000
                    per month, paid via bank transfer and activated by a Melian Dialogue admin after
                    verification, granting up to one hundred (100) client seats unless upgraded. All
                    client-side subscriptions are waived and covered by this plan.</p>

                    <p><strong>4. Client Data & Privacy.</strong> The Broker is the data controller of
                    their clients' personal data; Melian Dialogue is the data processor. All handling
                    complies with the Kenya Data Protection Act (2019).</p>

                    <p><strong>5. Prestige Mode.</strong> The platform currently operates in prestige-only
                    mode with no cash payouts, prize pools, or referral commissions. Recognition is
                    delivered via in-portal leaderboards, share cards, and Trader-of-the-Week features.
                    Should a cash reward programme be reintroduced, Melian Dialogue will publish
                    updated terms and secure separate Broker acknowledgement.</p>

                    <p><strong>6. Termination.</strong> Either party may terminate on 30 days written
                    notice. Non-payment of the monthly subscription for more than 14 days automatically
                    suspends the Agency and blocks new client onboarding until arrears are cleared.</p>

                    <p><strong>7. No Guarantee.</strong> The Platform makes no representation as to the
                    profitability, accuracy, or fitness of any simulated trading outcome. Trading, even
                    on a synthetic platform, involves the risk of forming poor habits transferable to
                    real markets. Users are responsible for their own decisions.</p>

                    <p><strong>8. Regulatory Notice.</strong> This Agreement, and the Platform, sit
                    outside CMA's licensed-brokerage regulatory perimeter by construction. The Broker
                    acknowledges that using the Platform does not extend or diminish the scope of
                    their own CMA licence.</p>

                    <p><strong>9. Governing Law.</strong> Kenyan law. Nairobi jurisdiction.</p>

                    <p className="pt-2 text-center text-muted-foreground">— End of Agreement —</p>
                </div>

                <button
                    type="button"
                    disabled={busy || !scrolled}
                    onClick={accept}
                    data-testid="broker-agreement-accept"
                    className="w-full py-2 rounded bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-50"
                >
                    {busy
                        ? "Acknowledging…"
                        : scrolled
                            ? "I acknowledge and accept the Broker Agreement"
                            : "Scroll to the end to enable"}
                </button>
            </DialogContent>
        </Dialog>
    );
}
